import { SfItem, SfToken, decodeSfDict, encodeSfDict, symbolToStr } from "@svta/cml-structured-field-values";
import { getBaseUrl, urlToRelativePath, uuid } from "@svta/cml-utils";

//#region src/CMCD_FORMATTER_MAP.ts
const roundValue = (value) => {
	if (value instanceof SfItem) return new SfItem(Math.round(value.value), value.params);
	return Math.round(value);
};
const toRounded = (value) => {
	if (Array.isArray(value)) return value.map(roundValue);
	return roundValue(value);
};
const toUrlSafe = (value, options) => {
	if (Array.isArray(value)) return value.map((item) => toUrlSafe(item, options));
	if (value instanceof SfItem && typeof value.value === "string") return new SfItem(toUrlSafe(value.value, options), value.params);
	else {
		if (options.baseUrl) value = urlToRelativePath(value, getBaseUrl(options.baseUrl));
		return options.version === 1 ? encodeURIComponent(value) : value;
	}
};
const hundredValue = (value) => {
	if (value instanceof SfItem) return new SfItem(Math.round(value.value / 100) * 100, value.params);
	return Math.round(value / 100) * 100;
};
const toHundred = (value) => {
	if (Array.isArray(value)) return value.map(hundredValue);
	return hundredValue(value);
};
const nor = (value, options) => {
	let norValue = value;
	if (options.version >= 2) {
		if (value instanceof SfItem && typeof value.value === "string") norValue = new SfItem([value]);
		else if (typeof value === "string") norValue = [value];
	}
	return toUrlSafe(norValue, options);
};
/**
* The default formatters for CMCD values.
*
* @public
*/
const CMCD_FORMATTER_MAP = {
	br: toRounded,
	d: toRounded,
	bl: toHundred,
	dl: toHundred,
	mtp: toHundred,
	nor,
	rtp: toHundred,
	tb: toRounded
};

//#endregion
//#region src/CMCD_V2.ts
/**
* CMCD Version 2
*
* @public
*/
const CMCD_V2 = 2;

//#endregion
//#region src/CmcdEventType.ts
/**
* CMCD event type for the 'bc' key (bitrate change).
*
* @public
*/
const CMCD_EVENT_BITRATE_CHANGE = "bc";
/**
* CMCD event type for the 'ps' key (play state change).
*
* @public
*/
const CMCD_EVENT_PLAY_STATE = "ps";
/**
* CMCD event type for the 'pr' key (playback rate change).
*
* @public
*/
const CMCD_EVENT_PLAYBACK_RATE = "pr";
/**
* CMCD event type for the 'e' key (error).
*
* @public
*/
const CMCD_EVENT_ERROR = "e";
/**
* CMCD event type for the 't' key (time interval).
*
* @public
*/
const CMCD_EVENT_TIME_INTERVAL = "t";
/**
* CMCD event type for the 'c' key (content ID).
*
* @public
*/
const CMCD_EVENT_CONTENT_ID = "c";
/**
* CMCD event type for the 'b' key (backgrounded mode).
*
* @public
*/
const CMCD_EVENT_BACKGROUNDED_MODE = "b";
/**
* CMCD event type for the 'm' key (mute).
*
* @public
*/
const CMCD_EVENT_MUTE = "m";
/**
* CMCD event type for the 'um' key (unmute).
*
* @public
*/
const CMCD_EVENT_UNMUTE = "um";
/**
* CMCD event type for the 'pe' key (player expand).
*
* @public
*/
const CMCD_EVENT_PLAYER_EXPAND = "pe";
/**
* CMCD event type for the 'pc' key (player collapse).
*
* @public
*/
const CMCD_EVENT_PLAYER_COLLAPSE = "pc";
/**
* CMCD event type for the 'rr' key (response received).
*
* @public
*/
const CMCD_EVENT_RESPONSE_RECEIVED = "rr";
/**
* CMCD event type for the 'as' key (ad start).
*
* @public
*/
const CMCD_EVENT_AD_START = "as";
/**
* CMCD event type for the 'ae' key (ad end).
*
* @public
*/
const CMCD_EVENT_AD_END = "ae";
/**
* CMCD event type for the 'abs' key (ad break start).
*
* @public
*/
const CMCD_EVENT_AD_BREAK_START = "abs";
/**
* CMCD event type for the 'abe' key (ad break end).
*
* @public
*/
const CMCD_EVENT_AD_BREAK_END = "abe";
/**
* CMCD event type for the 'sk' key (skip).
*
* @public
*/
const CMCD_EVENT_SKIP = "sk";
/**
* CMCD event type for the 'ce' key (custom event).
*
* @public
*/
const CMCD_EVENT_CUSTOM_EVENT = "ce";
/**
* CMCD event types for the 'e' key (event mode).
*
* @enum
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#event | CTA-5004-B Event}
*
* @public
*/
const CmcdEventType = {
	BITRATE_CHANGE: CMCD_EVENT_BITRATE_CHANGE,
	PLAY_STATE: CMCD_EVENT_PLAY_STATE,
	PLAYBACK_RATE: CMCD_EVENT_PLAYBACK_RATE,
	ERROR: CMCD_EVENT_ERROR,
	TIME_INTERVAL: CMCD_EVENT_TIME_INTERVAL,
	CONTENT_ID: CMCD_EVENT_CONTENT_ID,
	BACKGROUNDED_MODE: CMCD_EVENT_BACKGROUNDED_MODE,
	MUTE: CMCD_EVENT_MUTE,
	UNMUTE: CMCD_EVENT_UNMUTE,
	PLAYER_EXPAND: CMCD_EVENT_PLAYER_EXPAND,
	PLAYER_COLLAPSE: CMCD_EVENT_PLAYER_COLLAPSE,
	RESPONSE_RECEIVED: CMCD_EVENT_RESPONSE_RECEIVED,
	AD_START: CMCD_EVENT_AD_START,
	AD_END: CMCD_EVENT_AD_END,
	AD_BREAK_START: CMCD_EVENT_AD_BREAK_START,
	AD_BREAK_END: CMCD_EVENT_AD_BREAK_END,
	SKIP: CMCD_EVENT_SKIP,
	CUSTOM_EVENT: CMCD_EVENT_CUSTOM_EVENT
};

//#endregion
//#region src/CMCD_STATE_EVENT_FIELDS.ts
/**
* Maps each state-change event type to the persistent field whose value
* the event signals.
*
* Per CTA-5004-B, the state-change events `ps`, `pr`, `c`, `b`, `bc` are
* state-transition markers and must carry the field whose value they signal.
* Consumers force-include the field post-filter (`prepareCmcdData`), dedup
* against its value (`CmcdReporter`), and check its presence in payloads
* (`validateCmcdStructure`).
*
* Iteration order is load-bearing: `CmcdReporter.update()` fires state-change
* events in map order when multiple tracked fields change in a single call.
* Do not reorder entries without auditing reporter behavior.
*
* @internal
*/
const CMCD_STATE_EVENT_FIELDS = new Map([
	[CMCD_EVENT_PLAY_STATE, "sta"],
	[CMCD_EVENT_PLAYBACK_RATE, "pr"],
	[CMCD_EVENT_CONTENT_ID, "cid"],
	[CMCD_EVENT_BACKGROUNDED_MODE, "bg"],
	[CMCD_EVENT_BITRATE_CHANGE, "br"]
]);

//#endregion
//#region src/CmcdReportingMode.ts
/**
* CMCD event mode variable name.
*
* @public
*/
const CMCD_EVENT_MODE = "event";
/**
* CMCD request mode variable name.
*
* @public
*/
const CMCD_REQUEST_MODE = "request";
/**
* CMCD reporting mode types.
*
* @enum
*
* @public
*/
const CmcdReportingMode = {
	REQUEST: CMCD_REQUEST_MODE,
	EVENT: CMCD_EVENT_MODE
};

//#endregion
//#region src/CMCD_EVENT_KEYS.ts
/**
* Defines the event-specific keys for CMCD (Common Media Client Data) version 2.
*
* @public
*/
const CMCD_EVENT_KEYS = [
	"cen",
	"e",
	"h",
	"ts"
];

//#endregion
//#region src/CMCD_REQUEST_KEYS.ts
/**
* Defines the request-specific keys for CMCD (Common Media Client Data) version 2.
*
* @public
*/
const CMCD_REQUEST_KEYS = [
	"ab",
	"bg",
	"bl",
	"br",
	"bs",
	"bsa",
	"bsd",
	"bsda",
	"cdn",
	"cid",
	"cs",
	"d",
	"dfa",
	"dl",
	"ec",
	"lab",
	"lb",
	"ltc",
	"msd",
	"mtp",
	"nor",
	"nr",
	"ot",
	"pb",
	"pr",
	"pt",
	"rtp",
	"sf",
	"sid",
	"sn",
	"st",
	"sta",
	"su",
	"tab",
	"tb",
	"tbl",
	"tpb",
	"v"
];

//#endregion
//#region src/isCmcdCustomKey.ts
const CUSTOM_KEY_REGEX = /^[a-zA-Z0-9-.]+-[a-zA-Z0-9-.]+$/;
/**
* Check if a key is a custom key.
*
* @param key - The key to check.
*
* @returns `true` if the key is a custom key, `false` otherwise.
*
* @public
*/
function isCmcdCustomKey(key) {
	return CUSTOM_KEY_REGEX.test(key);
}

//#endregion
//#region src/isCmcdRequestKey.ts
const CMCD_REQUEST_KEY_SET = new Set(CMCD_REQUEST_KEYS);
/**
* Check if a key is a valid CMCD request key.
*
* @param key - The key to check.
*
* @returns `true` if the key is a valid CMCD request key, `false` otherwise.
*
* @public
*
* @example
* {@includeCode ../test/isCmcdRequestKey.test.ts#example}
*/
function isCmcdRequestKey(key) {
	return CMCD_REQUEST_KEY_SET.has(key) || isCmcdCustomKey(key);
}

//#endregion
//#region src/CMCD_RESPONSE_KEYS.ts
/**
* CMCD v2 - Response-only and timing keys.
*
* @public
*/
const CMCD_RESPONSE_KEYS = [
	"cmsdd",
	"cmsds",
	"rc",
	"smrt",
	"ttfb",
	"ttfbb",
	"ttlb",
	"url"
];

//#endregion
//#region src/isCmcdResponseReceivedKey.ts
const CMCD_RESPONSE_KEY_SET = new Set(CMCD_RESPONSE_KEYS);
/**
* Check if a key is a valid CMCD response key.
*
* @param key - The key to check.
*
* @returns `true` if the key is a valid CMCD request key, `false` otherwise.
*
* @public
*
* @example
* {@includeCode ../test/isCmcdResponseReceivedKey.test.ts#example}
*/
function isCmcdResponseReceivedKey(key) {
	return CMCD_RESPONSE_KEY_SET.has(key);
}

//#endregion
//#region src/isCmcdEventKey.ts
const CMCD_EVENT_KEY_SET = new Set(CMCD_EVENT_KEYS);
/**
* Check if a key is a valid CMCD event key.
*
* @param key - The key to check.
*
* @returns `true` if the key is a valid CMCD event key, `false` otherwise.
*
* @public
*
* @example
* {@includeCode ../test/isCmcdEventKey.test.ts#example}
*/
function isCmcdEventKey(key) {
	return isCmcdRequestKey(key) || isCmcdResponseReceivedKey(key) || CMCD_EVENT_KEY_SET.has(key);
}

//#endregion
//#region src/CMCD_INNER_LIST_KEYS.ts
/**
* Keys that are inner lists in V2 but plain scalars in V1.
*
* Used by both encoding (down-conversion) and decoding (up-conversion).
*
* @internal
*/
const CMCD_INNER_LIST_KEYS = new Set([
	"ab",
	"bl",
	"br",
	"bsa",
	"bsd",
	"bsda",
	"lab",
	"lb",
	"mtp",
	"pb",
	"tab",
	"tb",
	"tbl",
	"tpb"
]);

//#endregion
//#region src/CMCD_V1_KEYS.ts
/**
* Defines the keys for CMCD (Common Media Client Data) version 1.
*
* @public
*/
const CMCD_V1_KEYS = [
	"bl",
	"br",
	"bs",
	"cid",
	"d",
	"dl",
	"mtp",
	"nor",
	"nrr",
	"ot",
	"pr",
	"rtp",
	"sf",
	"sid",
	"st",
	"su",
	"tb",
	"v"
];

//#endregion
//#region src/isCmcdV1Key.ts
const CMCD_V1_KEY_SET$1 = new Set(CMCD_V1_KEYS);
/**
* Filter function for CMCD v1 keys.
*
* @param key - The CMCD key to filter.
*
* @returns `true` if the key should be included, `false` otherwise.
*
* @public
*
* @example
* {@includeCode ../test/isCmcdV1Key.test.ts#example}
*/
function isCmcdV1Key(key) {
	return CMCD_V1_KEY_SET$1.has(key) || isCmcdCustomKey(key);
}

//#endregion
//#region src/isTokenField.ts
const TOKEN_FIELDS = new Set([
	"ot",
	"sf",
	"st",
	"e",
	"sta"
]);
/**
* Checks if the given key is a token field.
*
* @param key - The key to check.
*
* @returns `true` if the key is a token field.
*
* @internal
*/
function isTokenField(key) {
	return TOKEN_FIELDS.has(key);
}

//#endregion
//#region src/isValid.ts
/**
* Checks if the given value is valid
*
* @param value - The value to check.
*
* @returns `true` if the key is a value is valid.
*
* @internal
*/
function isValid(value) {
	if (typeof value === "number") return Number.isFinite(value);
	return value != null && value !== "" && value !== false;
}

//#endregion
//#region src/prepareCmcdData.ts
const filterMap = {
	[CMCD_EVENT_MODE]: isCmcdEventKey,
	[CMCD_REQUEST_MODE]: isCmcdRequestKey
};
/**
* Unwrap an inner list or SfItem value to a plain scalar.
*/
function unwrapValue(value, ot) {
	if (Array.isArray(value)) {
		let item;
		if (ot) item = value.find((item$1) => item$1.params?.ot === ot);
		if (!item) item = value[0];
		return unwrapValue(item);
	}
	if (value instanceof SfItem) return value.value;
	return value;
}
/**
* Down-convert V2 CMCD data to V1 format.
*
* - Extracts `nrr` from `nor` SfItem `r` parameter.
* - Unwraps inner-list values to plain scalars.
*/
function downConvertToV1(obj) {
	const result = {};
	for (const [key, value] of Object.entries(obj)) {
		if (value == null) {
			result[key] = value;
			continue;
		}
		if (key === "nor") {
			const first = (Array.isArray(value) ? value : [value])[0];
			if (first instanceof SfItem) {
				result["nor"] = first.value;
				if (first.params?.r) result["nrr"] = first.params.r;
			} else result["nor"] = first;
		} else if (CMCD_INNER_LIST_KEYS.has(key)) result[key] = unwrapValue(value, obj["ot"]);
		else result[key] = value;
	}
	return result;
}
/**
* Convert a generic object to CMCD data.
*
* @param obj - The CMCD object to process.
* @param options - Options for encoding.
*
* @public
*/
function prepareCmcdData(obj, options = {}) {
	const results = {};
	if (obj == null || typeof obj !== "object") return results;
	const version = options.version || obj["v"] || CMCD_V2;
	const reportingMode = options.reportingMode || CMCD_REQUEST_MODE;
	const data = version === 1 ? downConvertToV1(obj) : obj;
	const keyFilter = version === 1 ? isCmcdV1Key : filterMap[reportingMode];
	let keys = Object.keys(data).filter(keyFilter);
	if (data["e"] && data["e"] !== CMCD_EVENT_RESPONSE_RECEIVED) keys = keys.filter((key) => !isCmcdResponseReceivedKey(key));
	const filter = options.filter;
	if (typeof filter === "function") keys = keys.filter(filter);
	const isEventMode = reportingMode === CMCD_EVENT_MODE;
	if (isEventMode) {
		const eventType = data["e"];
		if (!keys.includes("e") && eventType != null) keys.push("e");
		if (!keys.includes("ts")) keys.push("ts");
		if (!keys.includes("cen") && data["cen"] != null && eventType === CMCD_EVENT_CUSTOM_EVENT) keys.push("cen");
		const requiredField = eventType ? CMCD_STATE_EVENT_FIELDS.get(eventType) : void 0;
		if (requiredField && data[requiredField] != null && !keys.includes(requiredField)) keys.push(requiredField);
	}
	if (keys.length === 0) return results;
	if (version > 1 && !keys.includes("v")) keys.push("v");
	const formatterOptions = {
		version,
		reportingMode,
		baseUrl: options.baseUrl
	};
	keys.sort();
	for (const key of keys) {
		let value = data[key];
		const formatter = options.formatters?.[key] ?? CMCD_FORMATTER_MAP[key];
		if (typeof formatter === "function") value = formatter(value, formatterOptions);
		if (key === "v") if (version === 1) continue;
		else value = version;
		if (key === "pr" && value === 1 && !(isEventMode && data["e"] === CMCD_EVENT_PLAYBACK_RATE)) continue;
		if (isEventMode && key === "ts" && !Number.isFinite(value)) value = Date.now();
		const isBgFalseTransition = isEventMode && value === false && key === "bg" && data["e"] === CMCD_EVENT_BACKGROUNDED_MODE;
		if (!isValid(value) && !isBgFalseTransition) continue;
		if (isTokenField(key) && typeof value === "string") value = new SfToken(value);
		results[key] = value;
	}
	return results;
}

//#endregion
//#region src/CmcdHeaderField.ts
/**
* CMCD object header name.
*
* @public
*/
const CMCD_OBJECT = "CMCD-Object";
/**
* CMCD request header name.
*
* @public
*/
const CMCD_REQUEST = "CMCD-Request";
/**
* CMCD session header name.
*
* @public
*/
const CMCD_SESSION = "CMCD-Session";
/**
* CMCD status header name.
*
* @public
*/
const CMCD_STATUS = "CMCD-Status";
/**
* CMCD header fields.
*
*
* @enum
*
* @public
*/
const CmcdHeaderField = {
	OBJECT: CMCD_OBJECT,
	REQUEST: CMCD_REQUEST,
	SESSION: CMCD_SESSION,
	STATUS: CMCD_STATUS
};
/**
* All CMCD header fields as an array.
*
* @public
*/
const CMCD_HEADER_FIELDS = [
	CMCD_OBJECT,
	CMCD_REQUEST,
	CMCD_SESSION,
	CMCD_STATUS
];

//#endregion
//#region src/CMCD_HEADER_MAP.ts
/**
* The map of CMCD keys to their appropriate header shard.
*
* Note: Event-only keys (e, ts, cen, h) and response-received keys
* (rc, ttfb, ttlb, url, etc.) are intentionally absent. They are
* transmitted via the event-mode POST body, not HTTP headers.
*
* @public
*/
const CMCD_HEADER_MAP = {
	ab: CMCD_OBJECT,
	br: CMCD_OBJECT,
	d: CMCD_OBJECT,
	lab: CMCD_OBJECT,
	lb: CMCD_OBJECT,
	ot: CMCD_OBJECT,
	tab: CMCD_OBJECT,
	tb: CMCD_OBJECT,
	tpb: CMCD_OBJECT,
	bl: CMCD_REQUEST,
	cs: CMCD_REQUEST,
	dfa: CMCD_REQUEST,
	dl: CMCD_REQUEST,
	ltc: CMCD_REQUEST,
	mtp: CMCD_REQUEST,
	nor: CMCD_REQUEST,
	nrr: CMCD_REQUEST,
	pb: CMCD_REQUEST,
	sn: CMCD_REQUEST,
	sta: CMCD_REQUEST,
	su: CMCD_REQUEST,
	tbl: CMCD_REQUEST,
	cid: CMCD_SESSION,
	msd: CMCD_SESSION,
	sf: CMCD_SESSION,
	sid: CMCD_SESSION,
	st: CMCD_SESSION,
	v: CMCD_SESSION,
	bg: CMCD_STATUS,
	bs: CMCD_STATUS,
	bsa: CMCD_STATUS,
	bsd: CMCD_STATUS,
	bsda: CMCD_STATUS,
	cdn: CMCD_STATUS,
	ec: CMCD_STATUS,
	nr: CMCD_STATUS,
	pr: CMCD_STATUS,
	pt: CMCD_STATUS,
	rtp: CMCD_STATUS
};

//#endregion
//#region src/groupCmcdHeaders.ts
function createHeaderMap(headerMap) {
	return Object.keys(headerMap).reduce((acc, field) => {
		headerMap[field]?.forEach((key) => acc[key] = field);
		return acc;
	}, {});
}
/**
* Group a CMCD data object into header shards
*
* @param cmcd - The CMCD data object to convert.
* @param customHeaderMap - A map of CMCD header fields to custom CMCD keys.
*
* @returns The CMCD header shards.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#header-field-definition | CTA-5004-B Header Field Definition}
*
* @public
*/
function groupCmcdHeaders(cmcd, customHeaderMap) {
	const result = {};
	if (!cmcd) return result;
	const keys = Object.keys(cmcd);
	const custom = customHeaderMap ? createHeaderMap(customHeaderMap) : {};
	for (const key of keys) {
		const field = CMCD_HEADER_MAP[key] || custom[key] || CmcdHeaderField.REQUEST;
		const data = result[field] ??= {};
		data[key] = cmcd[key];
	}
	return result;
}

//#endregion
//#region src/toPreparedCmcdHeaders.ts
/**
* Encode already-prepared CMCD data to CMCD header shards.
*
* @param data - The prepared CMCD data to encode.
* @param customHeaderMap - A map of CMCD header fields to custom CMCD keys.
* @returns The CMCD header shards.
*
* @internal
*/
function toPreparedCmcdHeaders(data, customHeaderMap) {
	const result = {};
	const shards = groupCmcdHeaders(data, customHeaderMap);
	for (const [field, value] of Object.entries(shards)) {
		const shard = encodeSfDict(value, { whitespace: false });
		if (shard) result[field] = shard;
	}
	return result;
}

//#endregion
//#region src/toCmcdHeaders.ts
/**
* Convert a CMCD data object to request headers
*
* @param cmcd - The CMCD data object to convert.
* @param options - Options for encoding the CMCD object.
*
* @returns The CMCD header shards.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#header-field-definition | CTA-5004-B Header Field Definition}
*
* @public
*
* @example
* {@includeCode ../test/toCmcdHeaders.test.ts#example}
*/
function toCmcdHeaders(cmcd, options = {}) {
	if (!cmcd) return {};
	return toPreparedCmcdHeaders(prepareCmcdData(cmcd, options), options?.customHeaderMap);
}

//#endregion
//#region src/appendCmcdHeaders.ts
/**
* Append CMCD query args to a header object.
*
* @param headers - The headers to append to.
* @param cmcd - The CMCD object to append.
* @param options - Encode options.
*
* @returns The headers with the CMCD header shards appended.
*
* @public
*
* @example
* {@includeCode ../test/appendCmcdHeaders.test.ts#example}
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#header-field-definition | CTA-5004-B Header Field Definition}
*/
function appendCmcdHeaders(headers, cmcd, options) {
	return Object.assign(headers, toCmcdHeaders(cmcd, options));
}

//#endregion
//#region src/CMCD_PARAM.ts
/**
* CMCD parameter name.
*
* @public
*/
const CMCD_PARAM = "CMCD";

//#endregion
//#region src/encodePreparedCmcd.ts
/**
* Encode already-prepared CMCD data to a structured field dictionary string.
*
* @param data - The prepared CMCD data to encode.
* @returns The encoded CMCD string.
*
* @internal
*/
function encodePreparedCmcd(data) {
	return encodeSfDict(data, { whitespace: false });
}

//#endregion
//#region src/encodeCmcd.ts
/**
* Encode a CMCD object to a string.
*
* @param cmcd - The CMCD object to encode.
* @param options - Options for encoding.
*
* @returns The encoded CMCD string.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#payload-definition-for-headers-and-query-argument-transmission | CTA-5004-B Payload Definition}
*
* @public
*
* @example
* {@includeCode ../test/encodeCmcd.test.ts#example}
*/
function encodeCmcd(cmcd, options = {}) {
	if (!cmcd) return "";
	return encodePreparedCmcd(prepareCmcdData(cmcd, options));
}

//#endregion
//#region src/toCmcdUrl.ts
/**
* Convert a CMCD data object to a URL encoded string.
*
* @param cmcd - The CMCD object to convert.
* @param options - Options for encoding the CMCD object.
*
* @returns The URL encoded CMCD data.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#query-argument-definition | CTA-5004-B Query Argument Definition}
*
* @public
*/
function toCmcdUrl(cmcd, options = {}) {
	if (!cmcd) return "";
	const params = encodeCmcd(cmcd, options);
	return encodeURIComponent(params);
}

//#endregion
//#region src/toCmcdQuery.ts
/**
* Convert a CMCD data object to a query arg.
*
* @param cmcd - The CMCD object to convert.
* @param options - Options for encoding the CMCD object.
*
* @returns The CMCD query arg.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#query-argument-definition | CTA-5004-B Query Argument Definition}
*
* @public
*
* @example
* {@includeCode ../test/toCmcdQuery.test.ts#example}
*/
function toCmcdQuery(cmcd, options = {}) {
	if (!cmcd) return "";
	return `${CMCD_PARAM}=${toCmcdUrl(cmcd, options)}`;
}

//#endregion
//#region src/appendCmcdQuery.ts
const REGEX = /CMCD=[^&#]+/;
/**
* Append CMCD query args to a URL.
*
* @param url - The URL to append to.
* @param cmcd - The CMCD object to append.
* @param options - Options for encoding the CMCD object.
*
* @returns The URL with the CMCD query args appended.
*
* @public
*
* @example
* {@includeCode ../test/appendCmcdQuery.test.ts#example}
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#query-argument-definition | CTA-5004-B Query Argument Definition}
*/
function appendCmcdQuery(url, cmcd, options) {
	const query = toCmcdQuery(cmcd, options);
	if (!query) return url;
	if (REGEX.test(url)) return url.replace(REGEX, query);
	return `${url}${url.includes("?") ? "&" : "?"}${query}`;
}

//#endregion
//#region src/CMCD_DEFAULT_TIME_INTERVAL.ts
/**
* The default time interval in seconds when using using event mode
*
* @public
*/
const CMCD_DEFAULT_TIME_INTERVAL = 30;

//#endregion
//#region src/CMCD_KEYS.ts
/**
* A list of all CMCD keys.
*
* @public
*/
const CMCD_KEYS = [
	...CMCD_V1_KEYS,
	...CMCD_REQUEST_KEYS,
	...CMCD_RESPONSE_KEYS,
	...CMCD_EVENT_KEYS
].filter((key, index, arr) => arr.indexOf(key) === index);

//#endregion
//#region src/CMCD_MIME_TYPE.ts
/**
* CMCD MIME type for event report payloads.
*
* @public
*/
const CMCD_MIME_TYPE = "application/cmcd";

//#endregion
//#region src/CMCD_V1.ts
/**
* CMCD Version 1
*
* @public
*/
const CMCD_V1 = 1;

//#endregion
//#region src/CmcdObjectType.ts
/**
* Common Media Client Data Object Type
*
* @public
*
* @enum
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#object-type | CTA-5004-B Object Type}
*/
const CmcdObjectType = {
	MANIFEST: "m",
	AUDIO: "a",
	VIDEO: "v",
	MUXED: "av",
	INIT: "i",
	CAPTION: "c",
	TIMED_TEXT: "tt",
	KEY: "k",
	OTHER: "o"
};

//#endregion
//#region src/CmcdPlayerState.ts
/**
* CMCD v2 player states for the 'sta' key.
*
*
* @enum
*
* @public
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#state | CTA-5004-B State}
*/
const CmcdPlayerState = {
	STARTING: "s",
	PLAYING: "p",
	SEEKING: "k",
	REBUFFERING: "r",
	PAUSED: "a",
	WAITING: "w",
	ENDED: "e",
	FATAL_ERROR: "f",
	QUIT: "q",
	PRELOADING: "d"
};

//#endregion
//#region src/CmcdRecordedReportMode.ts
/**
* CMCD query reporting mode — CMCD data carried as a `CMCD=` URL
* query parameter.
*
* @public
*/
const CMCD_RECORDED_REPORT_MODE_QUERY = "query";
/**
* CMCD header reporting mode — CMCD data carried as `Cmcd-*` HTTP
* headers.
*
* @public
*/
const CMCD_RECORDED_REPORT_MODE_HEADER = "header";
/**
* CMCD event reporting mode — CMCD data carried as a POST body to an
* event-target URL.
*
* @public
*/
const CMCD_RECORDED_REPORT_MODE_EVENT = "event";
/**
* Reporting mode under which a captured report was observed by
* `CmcdReportRecorder`.
*
* @enum
*
* @public
*/
const CmcdRecordedReportMode = {
	QUERY: CMCD_RECORDED_REPORT_MODE_QUERY,
	HEADER: CMCD_RECORDED_REPORT_MODE_HEADER,
	EVENT: CMCD_RECORDED_REPORT_MODE_EVENT
};

//#endregion
//#region src/CmcdRecordedRequestType.ts
/**
* Manifest or playlist request classification.
*
* @public
*/
const CMCD_RECORDED_REQUEST_TYPE_MANIFEST = "manifest";
/**
* Media segment request classification.
*
* @public
*/
const CMCD_RECORDED_REQUEST_TYPE_SEGMENT = "segment";
/**
* CMCD event-mode report request classification (POST to a URL in
* `eventTargetUrls`).
*
* @public
*/
const CMCD_RECORDED_REQUEST_TYPE_EVENT = "event";
/**
* Fallback classification for requests the recorder did not recognize
* as manifest, segment, or event.
*
* @public
*/
const CMCD_RECORDED_REQUEST_TYPE_UNKNOWN = "unknown";
/**
* Classification of a captured request, used by `CmcdReportRecorder`
* for filtering.
*
* @enum
*
* @public
*/
const CmcdRecordedRequestType = {
	MANIFEST: CMCD_RECORDED_REQUEST_TYPE_MANIFEST,
	SEGMENT: CMCD_RECORDED_REQUEST_TYPE_SEGMENT,
	EVENT: CMCD_RECORDED_REQUEST_TYPE_EVENT,
	UNKNOWN: CMCD_RECORDED_REQUEST_TYPE_UNKNOWN
};

//#endregion
//#region src/createFetchTransport.ts
async function toHttpRequest(request) {
	const headers = {};
	request.headers.forEach((value, name) => {
		headers[name.toLowerCase()] = value;
	});
	let body;
	if (request.body) try {
		body = await request.text();
	} catch {
		body = void 0;
	}
	return {
		url: request.url,
		method: request.method,
		headers,
		body
	};
}
/**
* Create a transport adapter that patches `globalThis.fetch` to capture
* CMCD-bearing requests, normalizing each to `HttpRequest` (lowercase
* headers, body read as UTF-8 string). Returns the adapter object
* expected by `CmcdReportRecorder`.
*
* Bodies are read once via `Request.text()` for inspection. This is
* safe for the body types CMCD reports use in practice (`string`,
* `Blob`, `ArrayBuffer`, `FormData`, `URLSearchParams`), which can be
* read by the wrapper and re-read by the underlying `fetch`. Passing
* a `ReadableStream` as `init.body` is not supported — the stream is
* consumed by the wrapper and the underlying `fetch` will receive an
* already-disturbed stream.
*
* @public
*/
function createFetchTransport() {
	return { attach(deliver) {
		const origFetch = globalThis.fetch;
		globalThis.fetch = async (input, init) => {
			const synthetic = deliver(await toHttpRequest(input instanceof Request ? input.clone() : new Request(input, init)));
			if (synthetic) return synthetic;
			return origFetch.call(globalThis, input, init);
		};
		return () => {
			globalThis.fetch = origFetch;
		};
	} };
}

//#endregion
//#region src/createXhrTransport.ts
async function completeXhrWith(xhr, response) {
	const text = await response.clone().text().catch(() => "");
	const ProgressEventCtor = typeof ProgressEvent !== "undefined" ? ProgressEvent : Event;
	queueMicrotask(() => {
		try {
			Object.defineProperty(xhr, "status", {
				value: response.status,
				configurable: true
			});
			Object.defineProperty(xhr, "statusText", {
				value: response.statusText,
				configurable: true
			});
			Object.defineProperty(xhr, "readyState", {
				value: 4,
				configurable: true
			});
			Object.defineProperty(xhr, "responseURL", {
				value: xhr._cmcdUrl ?? "",
				configurable: true
			});
			Object.defineProperty(xhr, "response", {
				value: text,
				configurable: true
			});
			Object.defineProperty(xhr, "responseText", {
				value: text,
				configurable: true
			});
			if (typeof xhr.onload === "function") xhr.onload.call(xhr, new ProgressEventCtor("load"));
			if (typeof xhr.onloadend === "function") xhr.onloadend.call(xhr, new ProgressEventCtor("loadend"));
		} catch {
			const target = xhr;
			target["status"] = response.status;
			target["statusText"] = response.statusText;
			target["readyState"] = 4;
			target["responseURL"] = xhr._cmcdUrl ?? "";
			target["response"] = text;
			target["responseText"] = text;
			if (typeof xhr.onload === "function") xhr.onload.call(xhr, new ProgressEventCtor("load"));
			if (typeof xhr.onloadend === "function") xhr.onloadend.call(xhr, new ProgressEventCtor("loadend"));
		}
	});
}
/**
* Create a transport adapter that patches `XMLHttpRequest.prototype` to
* capture CMCD-bearing requests. Returns the adapter object expected by
* `CmcdReportRecorder`.
*
* @public
*/
function createXhrTransport() {
	return { attach(deliver) {
		const Xhr = globalThis.XMLHttpRequest;
		if (!Xhr) return () => {};
		const origOpen = Xhr.prototype.open;
		const origSetRequestHeader = Xhr.prototype.setRequestHeader;
		const origSend = Xhr.prototype.send;
		Xhr.prototype.open = function(method, url) {
			this._cmcdMethod = method;
			this._cmcdUrl = typeof url === "string" ? url : url.toString();
			this._cmcdHeaders = {};
			return origOpen.apply(this, arguments);
		};
		Xhr.prototype.setRequestHeader = function(name, value) {
			if (this._cmcdHeaders) this._cmcdHeaders[name.toLowerCase()] = value;
			return origSetRequestHeader.apply(this, [name, value]);
		};
		Xhr.prototype.send = function(body) {
			const synthetic = deliver({
				url: this._cmcdUrl ?? "",
				method: (this._cmcdMethod ?? "GET").toUpperCase(),
				headers: this._cmcdHeaders ?? {},
				body: (typeof Document !== "undefined" && body instanceof Document ? void 0 : body) ?? void 0
			});
			if (synthetic) {
				completeXhrWith(this, synthetic);
				return;
			}
			return origSend.apply(this, [body]);
		};
		return () => {
			Xhr.prototype.open = origOpen;
			Xhr.prototype.setRequestHeader = origSetRequestHeader;
			Xhr.prototype.send = origSend;
		};
	} };
}

//#endregion
//#region src/CmcdReportRecorder.ts
const MANIFEST_EXTENSIONS = /\.(m3u8|mpd)(\?|$|\/)/i;
const SEGMENT_EXTENSIONS = /\.(m4s|m4v|m4a|mp4|ts|aac)(\?|$|\/)/i;
function classifyUrl(url, isEventTarget) {
	if (isEventTarget) return CmcdRecordedRequestType.EVENT;
	if (MANIFEST_EXTENSIONS.test(url)) return CmcdRecordedRequestType.MANIFEST;
	if (SEGMENT_EXTENSIONS.test(url)) return CmcdRecordedRequestType.SEGMENT;
	return CmcdRecordedRequestType.UNKNOWN;
}
function hasCmcdHeader(headers) {
	if (!headers) return false;
	for (const name in headers) if (name.toLowerCase().startsWith("cmcd-")) return true;
	return false;
}
function detectReportingMode(url, headers, isEventTarget) {
	if (isEventTarget) return "event";
	if (hasCmcdHeader(headers)) return "header";
	return url.includes(`${CMCD_PARAM}=`) ? "query" : null;
}
/**
* Test helper that records CMCD-bearing reports across XHR and fetch
* transports for assertion in e2e tests. Each captured request is
* normalized to {@link @svta/cml-utils!HttpRequest | HttpRequest} so
* tests are identical regardless of which transport the player uses.
*
* @example
* {@includeCode ../test/CmcdReportRecorder.test.ts#example}
*
* @public
*/
var CmcdReportRecorder = class {
	#reports = [];
	#detachers = [];
	#attached = false;
	#eventTargetUrls = [];
	#waiters = /* @__PURE__ */ new Map();
	#onReport;
	#waitTimeout = 15e3;
	#deliver = (request) => {
		const url = request.url;
		const isEventTarget = (request.method ?? "GET").toUpperCase() === "POST" && this.#eventTargetUrls.some((t) => url.startsWith(t));
		const reportingMode = detectReportingMode(url, request.headers, isEventTarget);
		if (reportingMode === null) return;
		const captured = {
			request,
			type: classifyUrl(url, isEventTarget),
			reportingMode,
			timestamp: Date.now()
		};
		this.#reports.push(captured);
		if (this.#onReport) try {
			this.#onReport(captured);
		} catch (err) {
			console.error("CmcdReportRecorder onReport listener threw:", err);
		}
		this.#notifyWaiters();
		return isEventTarget ? new Response(null, { status: 204 }) : void 0;
	};
	#getMatching(type) {
		return type === void 0 ? [...this.#reports] : this.#reports.filter((r) => r.type === type);
	}
	#notifyWaiters() {
		for (const [timer, waiter] of this.#waiters) {
			const matching = this.#getMatching(waiter.type);
			if (matching.length >= waiter.count) {
				clearTimeout(timer);
				this.#waiters.delete(timer);
				waiter.resolve(matching);
			}
		}
	}
	#waitFor(type, options) {
		const count = options.count ?? 1;
		const timeout = options.timeout ?? this.#waitTimeout;
		const matching = this.#getMatching(type);
		if (matching.length >= count) return Promise.resolve(matching);
		return new Promise((resolve, reject) => {
			const timer = setTimeout(() => {
				this.#waiters.delete(timer);
				const current = this.#getMatching(type);
				reject(/* @__PURE__ */ new Error(`Timeout waiting for ${count} ${type ?? "any"} CMCD report(s). Got ${current.length}. Total recorded: ${this.#reports.length}.`));
			}, timeout);
			this.#waiters.set(timer, {
				type,
				count,
				resolve,
				reject
			});
		});
	}
	/**
	* Install transport patches and begin recording CMCD reports.
	* No-op if already attached — the previously supplied options are
	* retained and the new `options` argument is silently ignored.
	* Call `detach()` first if you need to re-attach with different
	* options.
	*
	* @public
	*/
	attach(options = {}) {
		if (this.#attached) return;
		this.#attached = true;
		this.#eventTargetUrls = options.eventTargetUrls ?? [];
		this.#onReport = options.onReport;
		this.#waitTimeout = options.waitTimeout ?? 15e3;
		const transports = options.transports ?? [createXhrTransport(), createFetchTransport()];
		for (const transport of transports) this.#detachers.push(transport.attach(this.#deliver));
	}
	/**
	* Remove transport patches and stop recording. Rejects any pending
	* wait promises with `Error('Recorder detached while waiting')`.
	*
	* @public
	*/
	detach() {
		if (!this.#attached) return;
		for (const detacher of this.#detachers) detacher();
		this.#detachers = [];
		this.#attached = false;
		this.#eventTargetUrls = [];
		this.#onReport = void 0;
		for (const [timer, waiter] of this.#waiters) {
			clearTimeout(timer);
			waiter.reject(/* @__PURE__ */ new Error("Recorder detached while waiting"));
		}
		this.#waiters.clear();
	}
	/**
	* Discard all recorded reports. Does not affect the attached state.
	*
	* @public
	*/
	clear() {
		this.#reports = [];
	}
	/**
	* Return a defensive copy of all recorded reports.
	*
	* @public
	*/
	getReports() {
		return [...this.#reports];
	}
	/**
	* Wait until at least `count` reports of any type are recorded.
	* Resolves with all matching reports; rejects with a diagnostic error
	* on timeout. `count` defaults to 1; `timeout` falls back to the
	* recorder's `waitTimeout` option (default 15000 ms).
	*
	* @public
	*/
	waitForReports(options = {}) {
		return this.#waitFor(void 0, options);
	}
	/**
	* Wait until at least `count` manifest reports are recorded.
	* Resolves with all matching reports; rejects with a diagnostic error
	* on timeout. `count` defaults to 1; `timeout` falls back to the
	* recorder's `waitTimeout` option (default 15000 ms).
	*
	* @public
	*/
	waitForManifest(options = {}) {
		return this.#waitFor(CmcdRecordedRequestType.MANIFEST, options);
	}
	/**
	* Wait until at least `count` segment reports are recorded.
	* Resolves with all matching reports; rejects with a diagnostic error
	* on timeout. `count` defaults to 1; `timeout` falls back to the
	* recorder's `waitTimeout` option (default 15000 ms).
	*
	* @public
	*/
	waitForSegments(options = {}) {
		return this.#waitFor(CmcdRecordedRequestType.SEGMENT, options);
	}
	/**
	* Wait until at least `count` event-mode reports are recorded.
	* Resolves with all matching reports; rejects with a diagnostic error
	* on timeout. `count` defaults to 1; `timeout` falls back to the
	* recorder's `waitTimeout` option (default 15000 ms).
	*
	* @public
	*/
	waitForEvents(options = {}) {
		return this.#waitFor(CmcdRecordedRequestType.EVENT, options);
	}
};

//#endregion
//#region src/CmcdTransmissionMode.ts
/**
* CMCD `query` transmission mode.
*
* @public
*/
const CMCD_QUERY = "query";
/**
* CMCD `headers` transmission mode.
*
* @public
*/
const CMCD_HEADERS = "headers";
/**
* CMCD `json` transmission mode.
*
* @public
*
* @deprecated JSON transmission mode is deprecated and will be removed in future versions.
*/
const CMCD_JSON = "json";
/**
* CMCD transmission modes.
*
* @enum
*
* @public
*/
const CmcdTransmissionMode = {
	JSON: CMCD_JSON,
	QUERY: CMCD_QUERY,
	HEADERS: CMCD_HEADERS
};

//#endregion
//#region src/CmcdReporter.ts
function createEncodingOptions(reportingMode, config, baseUrl) {
	const enabledKeySet = new Set(config.enabledKeys ?? []);
	return {
		version: config.version || CMCD_V2,
		reportingMode,
		filter: (key) => enabledKeySet.has(key),
		baseUrl
	};
}
/**
* Deep equality for CmcdObjectTypeList (used for `br` dedup).
*
* Order-sensitive: arrays with the same elements in different positions
* are treated as different. Players that construct `br` consistently
* get correct dedup; shuffling produces spurious emits, which is the
* safer failure mode.
*/
function cmcdObjectTypeListEqual(a, b) {
	if (a === b) return true;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		const ai = a[i];
		const bi = b[i];
		if (ai === bi) continue;
		if (typeof ai === "number" || typeof bi === "number") return false;
		if (ai.value !== bi.value) return false;
		const ap = ai.params;
		const bp = bi.params;
		const ak = ap && Object.keys(ap)[0];
		const bk = bp && Object.keys(bp)[0];
		if (ak !== bk) return false;
		if (ak !== void 0 && bk !== void 0 && ap && bp && ap[ak] !== bp[bk]) return false;
	}
	return true;
}
const equal = Object.is;
const identity = (v) => v;
/**
* Maps each tracked state field to its event type and equality function.
* Order matters: `update()` fires events in this order for multi-field updates.
*/
const STATE_FIELDS = Array.from(CMCD_STATE_EVENT_FIELDS, ([event, field]) => {
	if (field === "br") return {
		event,
		field,
		equal: (a, b) => a === void 0 || b === void 0 ? a === b : cmcdObjectTypeListEqual(a, b),
		snapshot: (v) => v.slice()
	};
	return {
		event,
		field,
		equal,
		snapshot: identity
	};
});
const STATE_FIELDS_BY_EVENT = new Map(STATE_FIELDS.map((e) => [e.event, e]));
function defaultRequester(request) {
	const { url,...init } = request;
	return fetch(url, init);
}
function createCmcdReporterConfig(config) {
	const { version = CMCD_V2, eventTargets = [], sid = uuid(), transmissionMode = CMCD_QUERY,...rest } = config;
	return {
		...rest,
		version,
		transmissionMode,
		sid,
		eventTargets: eventTargets.reduce((acc, target) => {
			if (target?.url && target.events?.length) acc.push({
				version: target.version || CMCD_V2,
				enabledKeys: target.enabledKeys?.slice() || [],
				url: target.url,
				events: target.events.slice(),
				interval: target.interval ?? CMCD_DEFAULT_TIME_INTERVAL,
				batchSize: target.batchSize || 1
			});
			return acc;
		}, [])
	};
}
/**
* The CMCD reporter.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#reporting-modes-when-we-send-data | CTA-5004-B Reporting Modes}
*
* @public
*/
var CmcdReporter = class {
	/**
	* Creates a new CMCD reporter.
	*
	* @param config - The configuration for the CMCD reporter.
	* @param requester - The function to use to send the request.
	*                    The default is a simple wrapper around the
	*                    native `fetch` API.
	*/
	constructor(config, requester = defaultRequester) {
		this.timeOrigin = performance.timeOrigin || performance.timing?.fetchStart || Date.now() - performance.now();
		this.data = {};
		this.msd = NaN;
		this.eventTargets = /* @__PURE__ */ new Map();
		this.lastEmitted = {};
		this.requestTarget = {
			sn: 0,
			msdSent: false
		};
		this.config = createCmcdReporterConfig(config);
		this.data = {
			cid: this.config.cid,
			sid: this.config.sid,
			v: this.config.version
		};
		this.requester = requester;
		for (const target of this.config.eventTargets) this.eventTargets.set(target, {
			intervalId: void 0,
			sn: 0,
			msdSent: false,
			queue: []
		});
	}
	/**
	* Starts the CMCD reporter. Called by the player when the reporter is enabled.
	*
	* Note: This fires an initial time-interval event immediately (synchronously)
	* before the first interval elapses. Ensure CMCD data (sid, cid, etc.) is
	* populated before calling start().
	*/
	start() {
		this.eventTargets.forEach((target, config) => {
			this.disarmInterval(target);
			if (config.interval === 0 || !config.events.includes(CMCD_EVENT_TIME_INTERVAL)) return;
			const timeIntervalEvent = () => {
				this.recordTargetEvent(target, config, CMCD_EVENT_TIME_INTERVAL);
				this.processEventTargets();
			};
			target.intervalId = setInterval(timeIntervalEvent, config.interval * 1e3);
			timeIntervalEvent();
		});
	}
	/**
	* Stops the CMCD reporter. Called by the player when the reporter is disabled.
	*
	* @param flush - Whether to flush the event targets.
	*/
	stop(flush = false) {
		if (flush) this.flush();
		this.eventTargets.forEach((target) => {
			this.disarmInterval(target);
		});
	}
	/**
	* Forces the sending of all event reports, regardless of the batch size or interval.
	* Useful for sending outstanding reports when the player is destroyed or a playback
	* session ends.
	*/
	flush() {
		this.processEventTargets(true);
	}
	/**
	* Updates the CMCD data.
	*
	* Called by the player when data changes. For tracked state fields
	* (`sta`, `pr`, `cid`, `bg`, `br`), if the new value differs from the
	* last-reported value (the value most recently emitted on the wire for
	* that field), the corresponding state-change event is automatically
	* fired. Comparing against the last-reported value (rather than the
	* previous persisted value) ensures the first state-change event in a
	* new session always emits, even when the persisted value didn't change
	* across the `sid` boundary.
	*
	* Multi-field updates fire multiple events in the order: `sta` → `pr` →
	* `cid` → `bg` → `br`. The order of keys in the input object does not
	* affect the firing order.
	*
	* A `sid` change resets the dedup baseline.
	*
	* @param data - The data to update.
	*/
	update(data) {
		if (data.sid && data.sid !== this.data.sid) this.resetSession();
		if (data.msd && !isNaN(data.msd)) this.msd = data.msd;
		this.data = {
			...this.data,
			...data,
			msd: void 0
		};
		for (const entry of STATE_FIELDS) if (entry.field in data && !entry.equal(this.data[entry.field], this.lastEmitted[entry.field])) this.recordEvent(entry.event);
	}
	/**
	* Records an event. Called by the player when an event occurs.
	*
	* For state-change events (`PLAY_STATE`, `PLAYBACK_RATE`, `CONTENT_ID`,
	* `BACKGROUNDED_MODE`, `BITRATE_CHANGE`), this method:
	* 1. Persists the dedup field from `data` (if present) into the reporter's
	*    persistent data store — equivalent to a write-through `update()`.
	* 2. Drops the event entirely if the dedup field has no value after the
	*    write-through (never set, or cleared via `update({ field: undefined })`).
	*    State-change events without their required field would violate CTA-5004-B.
	* 3. Suppresses the event if the field's current value matches the
	*    last-emitted value (no state transition).
	*
	* For all other event types, the event is always emitted.
	*
	* Most callers can rely on {@link CmcdReporter.update} to auto-fire state-change events.
	* Use `recordEvent` directly when you need to attach extra context to the
	* event report (e.g., `bl`, `pt` at the moment of a play-state transition).
	*
	* @param type - The type of event to record.
	* @param data - Additional data to record with the event. This data
	*               only applies to this event report, except for the dedup
	*               field of a state-change event, which is also persisted
	*               into the reporter's data store.
	*/
	recordEvent(type, data = {}) {
		const entry = STATE_FIELDS_BY_EVENT.get(type);
		if (entry) {
			const field = entry.field;
			const incoming = data[field];
			if (incoming !== void 0) Object.assign(this.data, { [field]: incoming });
			const current = this.data[field];
			if (current === void 0) return;
			if (entry.equal(current, this.lastEmitted[field])) return;
			Object.assign(this.lastEmitted, { [field]: entry.snapshot(current) });
		}
		this.eventTargets.forEach((target, config) => {
			this.recordTargetEvent(target, config, type, data);
		});
		this.processEventTargets();
	}
	/**
	* Records an event for a target. Called by the reporter when an event occurs.
	*
	* @param target - The target to record the event for.
	* @param config - The configuration for the target.
	* @param type - The type of event to record.
	* @param data - Additional data to record with the event. This data
	*               only applies to this event report. Persistent data should
	*               be updated using `update()`.
	*/
	recordTargetEvent(target, config, type, data = {}) {
		if (!config.events.includes(type)) return;
		const item = {
			...this.data,
			...data,
			e: type,
			ts: data.ts ?? Date.now(),
			sn: target.sn++
		};
		if (!isNaN(this.msd) && !target.msdSent) {
			item.msd = this.msd;
			target.msdSent = true;
		}
		target.queue.push(item);
	}
	/**
	* Records a response-received event. Called by the player when a media
	* request response has been fully received.
	*
	* This method automatically derives the `rr` event keys from the
	*
	* - `url` - the original requested URL (before any redirects)
	* - `rc` - the HTTP response status code
	* - `ts` - the request initiation time (from `resourceTiming.startTime`)
	* - `ttfb` - time to first byte (from `resourceTiming.responseStart`)
	* - `ttlb` - time to last byte (from `resourceTiming.duration`)
	*
	* Additional keys like `ttfbb`, `cmsdd`, `cmsds`, and `smrt` can be
	* supplied via the `data` parameter if the player has access to them.
	*
	* @param response - The HTTP response received.
	* @param data - Additional CMCD data to include with the event.
	*               Values provided here override any auto-derived values.
	*/
	recordResponseReceived(response, data = {}) {
		const { request } = response;
		const url = data.url ?? request?.url;
		if (!url) return;
		const urlObj = new URL(url);
		urlObj.searchParams.delete(CMCD_PARAM);
		const derived = {
			url: urlObj.toString(),
			rc: response.status
		};
		const timing = response.resourceTiming;
		if (timing) {
			if (timing.startTime != null) {
				derived.ts = Math.round(this.timeOrigin + timing.startTime);
				if (timing.responseStart != null) derived.ttfb = Math.round(timing.responseStart - timing.startTime);
			}
			if (timing.duration != null) derived.ttlb = Math.round(timing.duration);
		}
		const cmcd = request.customData?.cmcd ?? {};
		this.recordEvent(CMCD_EVENT_RESPONSE_RECEIVED, {
			...cmcd,
			...derived,
			...data
		});
	}
	/**
	* Applies the CMCD request report data to the request. Called by the player
	* before sending the request.
	*
	* @param req - The request to apply the CMCD request report to.
	* @returns The request with the CMCD request report applied.
	*
	* @deprecated Use {@link CmcdReporter.createRequestReport} instead.
	*/
	applyRequestReport(req) {
		return this.createRequestReport(req) ?? req;
	}
	/**
	* Checks if the request reporting is enabled.
	*
	* @returns `true` if the request reporting is enabled, `false` otherwise.
	*/
	isRequestReportingEnabled() {
		return !!this.config.enabledKeys?.length;
	}
	/**
	* Creates a new request with the CMCD request report data applied. Called by the player
	* before sending the request.
	*
	* @param request - The request to apply the CMCD request report to.
	* @param data - The data to apply to the request. This data only
	*               applies to this request report. Persistent data
	*               should be updated using `update()`.
	* @returns The request with the CMCD request report applied.
	*/
	createRequestReport(request, data) {
		const { customData = {}, headers = {},...rest } = request;
		const report = {
			...rest,
			headers: { ...headers },
			customData: {
				...customData,
				cmcd: {}
			}
		};
		if (!this.config.enabledKeys?.length || !report.url) return report;
		const url = new URL(report.url);
		const cmcdData = {
			...this.data,
			...data,
			sn: this.requestTarget.sn++
		};
		const options = createEncodingOptions(CMCD_REQUEST_MODE, this.config, report.url);
		if (!isNaN(this.msd) && !this.requestTarget.msdSent) {
			cmcdData.msd = this.msd;
			this.requestTarget.msdSent = true;
		}
		const cmcd = report.customData.cmcd = prepareCmcdData(cmcdData, options);
		switch (this.config.transmissionMode) {
			case CMCD_QUERY:
				const param = encodePreparedCmcd(cmcd);
				if (param) {
					url.searchParams.set(CMCD_PARAM, param);
					report.url = url.toString();
				}
				break;
			case CMCD_HEADERS:
				Object.assign(report.headers, toPreparedCmcdHeaders(cmcd, options.customHeaderMap));
				break;
		}
		return report;
	}
	/**
	* Processes the event targets. Called by the reporter when an event occurs.
	*
	* @param flush - Whether to flush the event targets.
	*/
	processEventTargets(flush = false) {
		let reprocess = false;
		this.eventTargets.forEach((target, config) => {
			const { queue } = target;
			if (!queue.length) return;
			if (queue.length < config.batchSize && !flush) return;
			const deleteCount = flush ? queue.length : config.batchSize;
			const events = queue.splice(0, deleteCount);
			this.sendEventReport(config, events).catch(() => {
				target.queue.unshift(...events);
			});
			reprocess ||= queue.length > 0;
		});
		if (reprocess) this.processEventTargets();
	}
	/**
	* Sends an event report. Called by the reporter when a batch is ready to be sent.
	*
	* @param config - The target config to send the event report to.
	* @param data - The data to send in the event report.
	*/
	async sendEventReport(config, data) {
		const options = createEncodingOptions(CMCD_EVENT_MODE, config);
		const { status } = await this.requester({
			url: config.url,
			method: "POST",
			headers: { "Content-Type": CMCD_MIME_TYPE },
			body: data.map((item) => encodeCmcd(item, options)).join("\n") + "\n"
		});
		if (status === 410) this.disposeEventTarget(config);
		else if (status === 429 || status > 499 && status < 600) throw new Error(`Event report failed with status ${status}`);
	}
	/**
	* Cancels the time-interval timer for an event target and clears the stored id.
	* Safe to call when no timer is armed (clearInterval(undefined) is a no-op).
	*/
	disarmInterval(target) {
		clearInterval(target.intervalId);
		target.intervalId = void 0;
	}
	/**
	* Permanently removes an event target: cancels its timer and removes it from the
	* eventTargets map. Used when the collector signals the target is gone (HTTP 410).
	*/
	disposeEventTarget(config) {
		const target = this.eventTargets.get(config);
		if (!target) return;
		this.disarmInterval(target);
		this.eventTargets.delete(config);
	}
	/**
	* Resets the session related data. Called when the session ID changes.
	*/
	resetSession() {
		this.eventTargets.forEach((target) => target.sn = 0);
		this.requestTarget.sn = 0;
		this.lastEmitted = {};
	}
};

//#endregion
//#region src/CmcdStreamingFormat.ts
/**
* Common Media Client Data Streaming Format
*
* @enum
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#streaming-format | CTA-5004-B Streaming Format}
*
* @public
*/
const CmcdStreamingFormat = {
	DASH: "d",
	HLS: "h",
	SMOOTH: "s",
	OTHER: "o"
};

//#endregion
//#region src/CmcdStreamType.ts
/**
* Common Media Client Data Stream Type
*
* @enum
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#stream-type | CTA-5004-B Stream Type}
*
* @public
*/
const CmcdStreamType = {
	VOD: "v",
	LIVE: "l",
	LOW_LATENCY: "ll"
};

//#endregion
//#region src/CmcdValidationSeverity.ts
/**
* CMCD validation severity level: error.
*
* @public
*/
const CMCD_VALIDATION_SEVERITY_ERROR = "error";
/**
* CMCD validation severity level: warning.
*
* @public
*/
const CMCD_VALIDATION_SEVERITY_WARNING = "warning";
/**
* CMCD validation severity level.
*
* @public
*/
const CmcdValidationSeverity = {
	ERROR: CMCD_VALIDATION_SEVERITY_ERROR,
	WARNING: CMCD_VALIDATION_SEVERITY_WARNING
};

//#endregion
//#region src/upConvertToV2.ts
/**
* Up-convert V1 CMCD data to V2 format.
*
* - Wraps plain scalar values in arrays for inner-list keys.
* - Wraps `nor` string in an array.
*
* If the data is already V2 (has `v: 2`), it is returned unchanged.
*
* @internal
*/
function upConvertToV2(obj) {
	if (obj["v"] === CMCD_V2) return obj;
	const result = {};
	for (const [key, value] of Object.entries(obj)) {
		if (value == null) {
			result[key] = value;
			continue;
		}
		if (CMCD_INNER_LIST_KEYS.has(key) && !Array.isArray(value)) result[key] = [value];
		else if (key === "nor" && typeof value === "string") result[key] = [value];
		else result[key] = value;
	}
	return result;
}

//#endregion
//#region src/decodeCmcd.ts
function reduceValue(value) {
	if (Array.isArray(value)) return value.map(reduceValue);
	if (typeof value === "symbol") return symbolToStr(value);
	if (value instanceof SfItem && !value.params) return reduceValue(value.value);
	if (typeof value === "string") return value;
	return value;
}
function decodeCmcd(cmcd, options) {
	if (!cmcd) return {};
	const sfDict = decodeSfDict(cmcd);
	const result = {};
	for (const [key, item] of Object.entries(sfDict)) result[key] = reduceValue(item.value);
	if (options?.convertToLatest) return upConvertToV2(result);
	return result;
}

//#endregion
//#region src/ensureHeaders.ts
/**
* Converts a record of header fields to a `Headers` instance if necessary.
*
* @param headers - A `Headers` instance or a plain record of header fields.
* @returns A `Headers` instance.
*
* @internal
*/
function ensureHeaders(headers) {
	return headers instanceof Headers ? headers : new Headers(headers);
}

//#endregion
//#region src/fromCmcdHeaders.ts
function fromCmcdHeaders(headers, options) {
	const h = ensureHeaders(headers);
	const result = {};
	for (const field of CMCD_HEADER_FIELDS) {
		const value = h.get(field);
		if (value) Object.assign(result, decodeCmcd(value));
	}
	if (options?.convertToLatest) return upConvertToV2(result);
	return result;
}

//#endregion
//#region src/fromCmcdQuery.ts
function fromCmcdQuery(query, options) {
	if (typeof query === "string") query = new URLSearchParams(query);
	return decodeCmcd(query.get(CMCD_PARAM) ?? "", options);
}

//#endregion
//#region src/fromCmcdUrl.ts
function fromCmcdUrl(url, options) {
	return decodeCmcd(decodeURIComponent(url.replace(/^CMCD=/, "")), options);
}

//#endregion
//#region src/isCmcdV1Data.ts
/**
* Check if a CMCD data object is version 1.
*
* @param data - The CMCD data object to check.
*
* @returns `true` if the data is version 1, `false` otherwise.
*
* @public
*
* @example
* {@includeCode ../test/isCmcdV1Data.test.ts#example}
*/
function isCmcdV1Data(data) {
	return data.v !== CMCD_V2;
}

//#endregion
//#region src/isCmcdV2Data.ts
/**
* Check if a CMCD data object is version 2.
*
* @param data - The CMCD data object to check.
*
* @returns `true` if the data is version 2, `false` otherwise.
*
* @public
*
* @example
* {@includeCode ../test/isCmcdV2Data.test.ts#example}
*/
function isCmcdV2Data(data) {
	return data.v === CMCD_V2;
}

//#endregion
//#region src/toCmcdValue.ts
/**
* Convert a value to a CMCD value.
*
* @param value - The value to convert to a CMCD value.
* @param params - The parameters to convert to a CMCD value.
* @returns The CMCD value.
*
* @public
*/
function toCmcdValue(value, params) {
	return new SfItem(value, params);
}

//#endregion
//#region src/mergeValidationResults.ts
/**
* Merges multiple validation results into a single result.
*
* @internal
*/
function mergeValidationResults(...results) {
	const issues = results.flatMap((r) => r.issues);
	return {
		valid: issues.every((i) => i.severity !== CMCD_VALIDATION_SEVERITY_ERROR),
		issues
	};
}

//#endregion
//#region src/resolveVersion.ts
/**
* Resolves the CMCD version from explicit options, the payload's `v` key, or the default (v1).
*
* @internal
*/
function resolveVersion(data, options) {
	if (options?.version === 1 || options?.version === 2) return options.version;
	const payloadVersion = data["v"];
	if (payloadVersion === 1 || payloadVersion === 2) return payloadVersion;
	return CMCD_V1;
}

//#endregion
//#region src/validateCmcdKeys.ts
const CMCD_V1_KEY_SET = new Set(CMCD_V1_KEYS);
const CMCD_KEY_SET = new Set(CMCD_KEYS);
/**
* Validates that all keys in a CMCD payload are recognized spec keys or valid custom keys.
*
* @example
* {@includeCode ../test/validateCmcdKeys.test.ts#example}
*
* @param data - The CMCD payload to validate.
* @param options - Validation options.
* @returns The validation result.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#reserved-keys | CTA-5004-B Reserved Keys}
*
* @public
*/
function validateCmcdKeys(data, options) {
	const version = resolveVersion(data, options);
	const validKeySet = version === CMCD_V1 ? CMCD_V1_KEY_SET : CMCD_KEY_SET;
	const issues = [];
	for (const key of Object.keys(data)) {
		if (isCmcdCustomKey(key)) continue;
		if (!validKeySet.has(key)) issues.push({
			key,
			message: `Unknown CMCD key "${key}" for version ${version}.`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
	}
	return {
		valid: issues.length === 0,
		issues
	};
}

//#endregion
//#region src/validateCmcdStructure.ts
/**
* Validates the structural rules of a CMCD payload.
*
* @example
* {@includeCode ../test/validateCmcdStructure.test.ts#example}
*
* @param data - The CMCD payload to validate.
* @param options - Validation options.
* @returns The validation result.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#data-payload-definition-what-data-to-send | CTA-5004-B Data Payload Definition}
*
* @public
*/
function validateCmcdStructure(data, options) {
	const version = resolveVersion(data, options);
	const issues = [];
	if (options?.reportingMode === CMCD_REQUEST_MODE) {
		for (const key of CMCD_EVENT_KEYS) if (key in data) issues.push({
			key,
			message: `Event key "${key}" must not be present in request mode.`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
		for (const key of CMCD_RESPONSE_KEYS) if (key in data) issues.push({
			key,
			message: `Response key "${key}" must not be present in request mode.`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
	}
	if (options?.reportingMode === CMCD_EVENT_MODE) {
		if (!("e" in data)) issues.push({
			key: "e",
			message: "Event mode requires the \"e\" key to be present.",
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
		if (!("ts" in data)) issues.push({
			key: "ts",
			message: "Event mode requires the \"ts\" key to be present.",
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
	}
	if ("e" in data) {
		if (data["e"] === CMCD_EVENT_CUSTOM_EVENT) {
			if (!("cen" in data)) issues.push({
				key: "cen",
				message: "Custom event (e=\"ce\") requires the \"cen\" key to be present.",
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			});
		} else if ("cen" in data) issues.push({
			key: "cen",
			message: "The \"cen\" key must only be present when e=\"ce\".",
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
		if (data["e"] === CMCD_EVENT_RESPONSE_RECEIVED) {
			if (!("url" in data)) issues.push({
				key: "url",
				message: "Response received event (e=\"rr\") requires the \"url\" key to be present.",
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			});
		} else for (const key of CMCD_RESPONSE_KEYS) if (key in data) issues.push({
			key,
			message: `Response key "${key}" must only be present when e="rr".`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
		for (const [eventType, requiredField] of CMCD_STATE_EVENT_FIELDS) if (data["e"] === eventType && !(requiredField in data)) issues.push({
			key: requiredField,
			message: `State-change event (e="${eventType}") requires the "${requiredField}" key to be present.`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
		if (data["e"] === CMCD_EVENT_ERROR && !("ec" in data)) issues.push({
			key: "ec",
			message: "Error event (e=\"e\") requires the \"ec\" key to be present.",
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
	}
	if ("v" in data && data["v"] !== 1 && data["v"] !== 2) issues.push({
		key: "v",
		message: `Unsupported CMCD version "${String(data["v"])}". Expected 1 or 2.`,
		severity: CMCD_VALIDATION_SEVERITY_ERROR
	});
	else if (version > 1 && !("v" in data)) issues.push({
		key: "v",
		message: "Version 2 payloads require the \"v\" key to be present.",
		severity: CMCD_VALIDATION_SEVERITY_ERROR
	});
	else if (version === CMCD_V1 && "v" in data) issues.push({
		key: "v",
		message: "Version 1 payloads should omit the \"v\" key (v1 is the default).",
		severity: CMCD_VALIDATION_SEVERITY_WARNING
	});
	return {
		valid: issues.every((i) => i.severity !== CMCD_VALIDATION_SEVERITY_ERROR),
		issues
	};
}

//#endregion
//#region src/CMCD_KEY_TYPES.ts
/**
* CMCD key value type: inner list of numbers with token identifiers.
*
* @internal
*/
const CMCD_KEY_TYPE_NUMBER_LIST = "number[]";
/**
* CMCD key value type: inner list of strings.
*
* @internal
*/
const CMCD_KEY_TYPE_STRING_LIST = "string[]";
/**
* CMCD key value type: integer.
*
* @internal
*/
const CMCD_KEY_TYPE_INTEGER = "integer";
/**
* CMCD key value type: number (decimal).
*
* @internal
*/
const CMCD_KEY_TYPE_NUMBER = "number";
/**
* CMCD key value type: boolean.
*
* @internal
*/
const CMCD_KEY_TYPE_BOOLEAN = "boolean";
/**
* CMCD key value type: string.
*
* @internal
*/
const CMCD_KEY_TYPE_STRING = "string";
/**
* CMCD key value type: token.
*
* @internal
*/
const CMCD_KEY_TYPE_TOKEN = "token";
/**
* Maps each CMCD spec key to its expected value type for v2.
* Keys that differ between v1 and v2 are handled by CMCD_V1_KEY_TYPE_OVERRIDES.
*
* @internal
*/
const CMCD_KEY_TYPES = {
	ab: CMCD_KEY_TYPE_NUMBER_LIST,
	bl: CMCD_KEY_TYPE_NUMBER_LIST,
	br: CMCD_KEY_TYPE_NUMBER_LIST,
	bsa: CMCD_KEY_TYPE_NUMBER_LIST,
	bsd: CMCD_KEY_TYPE_NUMBER_LIST,
	bsda: CMCD_KEY_TYPE_NUMBER_LIST,
	lab: CMCD_KEY_TYPE_NUMBER_LIST,
	lb: CMCD_KEY_TYPE_NUMBER_LIST,
	mtp: CMCD_KEY_TYPE_NUMBER_LIST,
	pb: CMCD_KEY_TYPE_NUMBER_LIST,
	tab: CMCD_KEY_TYPE_NUMBER_LIST,
	tb: CMCD_KEY_TYPE_NUMBER_LIST,
	tbl: CMCD_KEY_TYPE_NUMBER_LIST,
	tpb: CMCD_KEY_TYPE_NUMBER_LIST,
	ec: CMCD_KEY_TYPE_STRING_LIST,
	nor: CMCD_KEY_TYPE_STRING_LIST,
	d: CMCD_KEY_TYPE_INTEGER,
	dfa: CMCD_KEY_TYPE_INTEGER,
	dl: CMCD_KEY_TYPE_INTEGER,
	ltc: CMCD_KEY_TYPE_INTEGER,
	msd: CMCD_KEY_TYPE_INTEGER,
	pt: CMCD_KEY_TYPE_INTEGER,
	rc: CMCD_KEY_TYPE_INTEGER,
	rtp: CMCD_KEY_TYPE_INTEGER,
	sn: CMCD_KEY_TYPE_INTEGER,
	ts: CMCD_KEY_TYPE_INTEGER,
	ttfb: CMCD_KEY_TYPE_INTEGER,
	ttfbb: CMCD_KEY_TYPE_INTEGER,
	ttlb: CMCD_KEY_TYPE_INTEGER,
	v: CMCD_KEY_TYPE_INTEGER,
	pr: CMCD_KEY_TYPE_NUMBER,
	bg: CMCD_KEY_TYPE_BOOLEAN,
	bs: CMCD_KEY_TYPE_BOOLEAN,
	nr: CMCD_KEY_TYPE_BOOLEAN,
	su: CMCD_KEY_TYPE_BOOLEAN,
	cdn: CMCD_KEY_TYPE_STRING,
	cen: CMCD_KEY_TYPE_STRING,
	cid: CMCD_KEY_TYPE_STRING,
	cmsdd: CMCD_KEY_TYPE_STRING,
	cmsds: CMCD_KEY_TYPE_STRING,
	cs: CMCD_KEY_TYPE_STRING,
	h: CMCD_KEY_TYPE_STRING,
	nrr: CMCD_KEY_TYPE_STRING,
	sid: CMCD_KEY_TYPE_STRING,
	smrt: CMCD_KEY_TYPE_STRING,
	url: CMCD_KEY_TYPE_STRING,
	e: CMCD_KEY_TYPE_TOKEN,
	ot: CMCD_KEY_TYPE_TOKEN,
	sf: CMCD_KEY_TYPE_TOKEN,
	st: CMCD_KEY_TYPE_TOKEN,
	sta: CMCD_KEY_TYPE_TOKEN
};
/**
* Maps keys to their v1-specific types when they differ from v2.
*
* @internal
*/
const CMCD_V1_KEY_TYPE_OVERRIDES = {
	bl: CMCD_KEY_TYPE_INTEGER,
	br: CMCD_KEY_TYPE_NUMBER,
	mtp: CMCD_KEY_TYPE_INTEGER,
	tb: CMCD_KEY_TYPE_NUMBER,
	nor: CMCD_KEY_TYPE_STRING
};

//#endregion
//#region src/CMCD_STRING_LENGTH_LIMITS.ts
/**
* Maps CMCD keys to their maximum string length.
*
* @internal
*/
const CMCD_STRING_LENGTH_LIMITS = {
	sid: 64,
	cid: 128,
	cdn: 128,
	h: 128,
	cen: 64
};
/**
* Maximum length for custom key values.
*
* @internal
*/
const CMCD_CUSTOM_KEY_VALUE_MAX_LENGTH = 64;

//#endregion
//#region src/CMCD_TOKEN_VALUES.ts
/**
* Maps token keys to their valid values.
*
* @internal
*/
const CMCD_TOKEN_VALUES = {
	e: [
		"bc",
		"ps",
		"pr",
		"e",
		"t",
		"c",
		"b",
		"m",
		"um",
		"pe",
		"pc",
		"rr",
		"as",
		"ae",
		"abs",
		"abe",
		"sk",
		"ce"
	],
	ot: [
		"m",
		"a",
		"v",
		"av",
		"i",
		"c",
		"tt",
		"k",
		"o"
	],
	sf: [
		"d",
		"h",
		"s",
		"o"
	],
	st: [
		"v",
		"l",
		"ll"
	],
	sta: [
		"s",
		"p",
		"k",
		"r",
		"a",
		"w",
		"e",
		"f",
		"q",
		"d"
	]
};

//#endregion
//#region src/validateCmcdValues.ts
const HUNDRED_ROUNDING_KEYS = new Set([
	"bl",
	"dl",
	"mtp",
	"rtp",
	"tbl"
]);
const INTEGER_ROUNDING_KEYS = new Set([
	"br",
	"d",
	"tb"
]);
function isFiniteNumber(value) {
	return typeof value === "number" && Number.isFinite(value);
}
function validateListValue(key, value, issues) {
	if (!Array.isArray(value)) {
		issues.push({
			key,
			message: `Key "${key}" must be an array.`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
		return;
	}
	for (let i = 0; i < value.length; i++) {
		const element = value[i];
		if (element instanceof SfItem) {
			if (!isFiniteNumber(element.value)) issues.push({
				key,
				message: `Key "${key}" array element [${i}] must be a finite number.`,
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			});
		} else if (!isFiniteNumber(element)) issues.push({
			key,
			message: `Key "${key}" array element [${i}] must be a finite number.`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
	}
}
function validateStringArrayValue(key, value, issues) {
	if (!Array.isArray(value)) {
		issues.push({
			key,
			message: `Key "${key}" must be an array.`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
		return;
	}
	for (let i = 0; i < value.length; i++) {
		const element = value[i];
		if (element instanceof SfItem) {
			if (typeof element.value !== "string") issues.push({
				key,
				message: `Key "${key}" array element [${i}] must be a string.`,
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			});
		} else if (typeof element !== "string") issues.push({
			key,
			message: `Key "${key}" array element [${i}] must be a string.`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
	}
}
/**
* Validates that all values in a CMCD payload conform to the expected types and constraints.
*
* @example
* {@includeCode ../test/validateCmcdValues.test.ts#example}
*
* @param data - The CMCD payload to validate.
* @param options - Validation options.
* @returns The validation result.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#reserved-keys | CTA-5004-B Reserved Keys}
*
* @public
*/
function validateCmcdValues(data, options) {
	const version = resolveVersion(data, options);
	const issues = [];
	for (const [key, value] of Object.entries(data)) {
		if (isCmcdCustomKey(key)) {
			if (typeof value !== "string") issues.push({
				key,
				message: `Custom key "${key}" value must be a string or token.`,
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			});
			else if (value.length > CMCD_CUSTOM_KEY_VALUE_MAX_LENGTH) issues.push({
				key,
				message: `Custom key "${key}" value exceeds maximum length of ${CMCD_CUSTOM_KEY_VALUE_MAX_LENGTH}.`,
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			});
			continue;
		}
		if (key === "v") {
			if (value !== 1 && value !== 2) issues.push({
				key,
				message: `Key "v" must be 1 or 2.`,
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			});
			continue;
		}
		let expectedType = CMCD_KEY_TYPES[key];
		if (!expectedType) continue;
		if (version === CMCD_V1 && key in CMCD_V1_KEY_TYPE_OVERRIDES) expectedType = CMCD_V1_KEY_TYPE_OVERRIDES[key];
		switch (expectedType) {
			case CMCD_KEY_TYPE_INTEGER:
				if (!isFiniteNumber(value) || !Number.isInteger(value)) issues.push({
					key,
					message: `Key "${key}" must be a finite integer.`,
					severity: CMCD_VALIDATION_SEVERITY_ERROR
				});
				else if (HUNDRED_ROUNDING_KEYS.has(key) && value % 100 !== 0) issues.push({
					key,
					message: `Key "${key}" should be rounded to the nearest 100.`,
					severity: CMCD_VALIDATION_SEVERITY_WARNING
				});
				break;
			case CMCD_KEY_TYPE_NUMBER:
				if (!isFiniteNumber(value)) issues.push({
					key,
					message: `Key "${key}" must be a finite number.`,
					severity: CMCD_VALIDATION_SEVERITY_ERROR
				});
				else if (HUNDRED_ROUNDING_KEYS.has(key) && value % 100 !== 0) issues.push({
					key,
					message: `Key "${key}" should be rounded to the nearest 100.`,
					severity: CMCD_VALIDATION_SEVERITY_WARNING
				});
				else if (INTEGER_ROUNDING_KEYS.has(key) && !Number.isInteger(value)) issues.push({
					key,
					message: `Key "${key}" should be rounded to an integer.`,
					severity: CMCD_VALIDATION_SEVERITY_WARNING
				});
				break;
			case CMCD_KEY_TYPE_BOOLEAN:
				if (typeof value !== "boolean") issues.push({
					key,
					message: `Key "${key}" must be a boolean.`,
					severity: CMCD_VALIDATION_SEVERITY_ERROR
				});
				break;
			case CMCD_KEY_TYPE_STRING:
				if (typeof value !== "string") issues.push({
					key,
					message: `Key "${key}" must be a string.`,
					severity: CMCD_VALIDATION_SEVERITY_ERROR
				});
				else if (key in CMCD_STRING_LENGTH_LIMITS && value.length > CMCD_STRING_LENGTH_LIMITS[key]) issues.push({
					key,
					message: `Key "${key}" exceeds maximum length of ${CMCD_STRING_LENGTH_LIMITS[key]}.`,
					severity: CMCD_VALIDATION_SEVERITY_ERROR
				});
				break;
			case CMCD_KEY_TYPE_TOKEN: {
				const validValues = CMCD_TOKEN_VALUES[key];
				if (validValues && !validValues.includes(value)) issues.push({
					key,
					message: `Key "${key}" has invalid token value "${String(value)}". Expected one of: ${validValues.join(", ")}.`,
					severity: CMCD_VALIDATION_SEVERITY_ERROR
				});
				break;
			}
			case CMCD_KEY_TYPE_NUMBER_LIST:
				validateListValue(key, value, issues);
				break;
			case CMCD_KEY_TYPE_STRING_LIST:
				validateStringArrayValue(key, value, issues);
				break;
		}
	}
	return {
		valid: issues.every((i) => i.severity !== CMCD_VALIDATION_SEVERITY_ERROR),
		issues
	};
}

//#endregion
//#region src/validateCmcd.ts
/**
* Validates a CMCD payload by checking keys, values, and structure.
*
* @example
* {@includeCode ../test/validateCmcd.test.ts#example}
*
* @param data - The CMCD payload to validate.
* @param options - Validation options.
* @returns The validation result.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#data-payload-definition-what-data-to-send | CTA-5004-B Data Payload Definition}
*
* @public
*/
function validateCmcd(data, options) {
	return mergeValidationResults(validateCmcdKeys(data, options), validateCmcdValues(data, options), validateCmcdStructure(data, options));
}

//#endregion
//#region src/validateCmcdEvents.ts
/**
* Validates a raw CMCD string as an event-mode payload.
*
* This function decodes the string internally and validates it with
* `reportingMode` set to `'event'`. The input may contain multiple
* newline-separated events (e.g. an `application/cmcd` POST body), in which
* case each line is validated independently and the results are merged.
*
* @param cmcd - The raw CMCD-encoded string to validate. May contain
*   multiple newline-separated event lines.
* @param options - Validation options (excluding `reportingMode`).
* @returns The validation result including decoded data per event line.
*
* @example
* {@includeCode ../test/validateCmcdEvents.test.ts#example}
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#body-definition | CTA-5004-B Body Definition}
*
* @public
*/
function validateCmcdEvents(cmcd, options) {
	const opts = {
		...options,
		reportingMode: CMCD_EVENT_MODE
	};
	const lines = cmcd.split(/\r?\n/).filter((line) => line.length > 0);
	if (lines.length === 0) return {
		valid: false,
		issues: [{
			message: "Empty event mode payload. At least one event line is required.",
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		}],
		data: []
	};
	const decodedLines = [];
	const lineResults = [];
	for (let i = 0; i < lines.length; i++) try {
		const data = decodeCmcd(lines[i]);
		decodedLines.push(data);
		lineResults.push(validateCmcd(data, opts));
	} catch {
		decodedLines.push({});
		lineResults.push({
			valid: false,
			issues: [{
				message: `Failed to decode event line ${i + 1}: invalid structured field syntax.`,
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			}]
		});
	}
	return {
		...mergeValidationResults(...lineResults),
		data: decodedLines
	};
}

//#endregion
//#region src/validateCmcdEventReport.ts
/**
* Validates a full HTTP request as an event-mode payload.
*
* Accepts an {@link @svta/cml-utils!HttpRequest | HttpRequest} object.
*
* This function validates that the request uses the POST method and has
* the correct `Content-Type` header (`application/cmcd`) in addition to
* validating the body content via {@link validateCmcdEvents}.
*
* @param request - An `HttpRequest` to validate.
* @param options - Validation options (excluding `reportingMode`).
* @returns The validation result including decoded data per event line.
*
* @example {@includeCode ../test/validateCmcdEventReport.test.ts#example}
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#event-mode | CTA-5004-B Event Mode}
*
* @public
*/
function validateCmcdEventReport(request, options) {
	const issues = [];
	if (request.method !== "POST") issues.push({
		message: `Invalid HTTP method '${request.method ?? "GET"}'. Event reports must use POST.`,
		severity: CMCD_VALIDATION_SEVERITY_ERROR
	});
	const contentType = request.headers ? ensureHeaders(request.headers).get("Content-Type") : void 0;
	if (!contentType) issues.push({
		message: `Missing Content-Type header. Event reports must use '${CMCD_MIME_TYPE}'.`,
		severity: CMCD_VALIDATION_SEVERITY_ERROR
	});
	else {
		const mediaType = contentType.split(";")[0].trim().toLowerCase();
		if (mediaType !== CMCD_MIME_TYPE) issues.push({
			message: `Invalid Content-Type '${mediaType}'. Event reports must use '${CMCD_MIME_TYPE}'.`,
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
	}
	const body = request.body;
	if (body === void 0 || body === null) {
		issues.push({
			message: "Missing request body. Event reports must include a body.",
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
		return {
			valid: false,
			issues,
			data: []
		};
	}
	if (typeof body !== "string") {
		issues.push({
			message: "Request body must be a string.",
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		});
		return {
			valid: false,
			issues,
			data: []
		};
	}
	const bodyResult = validateCmcdEvents(body, options);
	return {
		valid: issues.length === 0 && bodyResult.valid,
		issues: [...issues, ...bodyResult.issues],
		data: bodyResult.data
	};
}

//#endregion
//#region src/validateCmcdHeaders.ts
/**
* Validates CMCD HTTP headers by checking shard placement and payload validity.
*
* This function accepts raw CMCD header strings, decodes each shard
* internally, verifies that each key is placed in its correct header shard,
* then merges all shards and runs full payload validation (keys, values, and
* structure) on the merged data.
*
* @example
* {@includeCode ../test/validateCmcdHeaders.test.ts#example}
*
* @param headers - A `Headers` instance or a record of CMCD header fields to their raw encoded string values.
* @param options - Validation options (excluding `reportingMode`).
* @returns The validation result including decoded data.
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#header-field-definition | CTA-5004-B Header Field Definition}
*
* @public
*/
function validateCmcdHeaders(headers, options) {
	const h = ensureHeaders(headers);
	const issues = [];
	const decoded = {};
	for (const headerField of CMCD_HEADER_FIELDS) {
		const raw = h.get(headerField);
		if (!raw) continue;
		let shard;
		try {
			shard = decodeCmcd(raw);
		} catch {
			issues.push({
				key: headerField,
				message: `Failed to decode "${headerField}" header: invalid structured field syntax.`,
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			});
			continue;
		}
		decoded[headerField] = shard;
		for (const key of Object.keys(shard)) {
			if (isCmcdCustomKey(key)) continue;
			const expectedHeader = CMCD_HEADER_MAP[key];
			if (expectedHeader && expectedHeader !== headerField) issues.push({
				key,
				message: `Key "${key}" is in "${headerField}" but should be in "${expectedHeader}".`,
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			});
		}
	}
	const shardResult = {
		valid: issues.length === 0,
		issues
	};
	const merged = Object.assign({}, ...CMCD_HEADER_FIELDS.map((f) => decoded[f]).filter(Boolean));
	return {
		...mergeValidationResults(shardResult, validateCmcd(merged, {
			...options,
			reportingMode: CMCD_REQUEST_MODE
		})),
		data: merged
	};
}

//#endregion
//#region src/validateCmcdRequest.ts
/**
* Validates CMCD data from a request as a request-mode payload.
*
* Accepts a
* {@link https://developer.mozilla.org/en-US/docs/Web/API/Request | Request}
* object or an {@link @svta/cml-utils!HttpRequest | HttpRequest} object.
*
* The function checks for CMCD data in the HTTP headers first. If CMCD
* headers are found, validation includes shard-placement checks via
* {@link validateCmcdHeaders}. Otherwise, the CMCD query parameter is
* extracted from the URL and validated.
*
* @param request - A `Request` or `HttpRequest` to validate.
* @param options - Validation options (excluding `reportingMode`).
* @returns The validation result including decoded data.
*
* @example
* {@includeCode ../test/validateCmcdRequest.test.ts#example}
*
* @see {@link https://cta-wave.github.io/Resources/common-media-client-data--cta-5004-b.html#request-mode | CTA-5004-B Request Mode}
*
* @public
*/
function validateCmcdRequest(request, options) {
	const headers = extractHeaderRecord(request.headers);
	if (headers) return validateCmcdHeaders(headers, options);
	const param = new URL(request.url).searchParams.get(CMCD_PARAM);
	if (!param) return {
		valid: false,
		issues: [{
			message: "No CMCD data found in request headers or query parameters.",
			severity: CMCD_VALIDATION_SEVERITY_ERROR
		}],
		data: {}
	};
	let data;
	try {
		data = decodeCmcd(param);
	} catch {
		return {
			valid: false,
			issues: [{
				message: "Failed to decode CMCD query parameter: invalid structured field syntax.",
				severity: CMCD_VALIDATION_SEVERITY_ERROR
			}],
			data: {}
		};
	}
	return {
		...validateCmcd(data, {
			...options,
			reportingMode: CMCD_REQUEST_MODE
		}),
		data
	};
}
function extractHeaderRecord(headers) {
	if (!headers) return;
	const h = ensureHeaders(headers);
	const result = {};
	let found = false;
	for (const field of CMCD_HEADER_FIELDS) {
		const value = h.get(field);
		if (value) {
			result[field] = value;
			found = true;
		}
	}
	return found ? result : void 0;
}

//#endregion
export { CMCD_DEFAULT_TIME_INTERVAL, CMCD_EVENT_AD_BREAK_END, CMCD_EVENT_AD_BREAK_START, CMCD_EVENT_AD_END, CMCD_EVENT_AD_START, CMCD_EVENT_BACKGROUNDED_MODE, CMCD_EVENT_BITRATE_CHANGE, CMCD_EVENT_CONTENT_ID, CMCD_EVENT_CUSTOM_EVENT, CMCD_EVENT_ERROR, CMCD_EVENT_KEYS, CMCD_EVENT_MODE, CMCD_EVENT_MUTE, CMCD_EVENT_PLAYBACK_RATE, CMCD_EVENT_PLAYER_COLLAPSE, CMCD_EVENT_PLAYER_EXPAND, CMCD_EVENT_PLAY_STATE, CMCD_EVENT_RESPONSE_RECEIVED, CMCD_EVENT_SKIP, CMCD_EVENT_TIME_INTERVAL, CMCD_EVENT_UNMUTE, CMCD_FORMATTER_MAP, CMCD_HEADERS, CMCD_HEADER_FIELDS, CMCD_HEADER_MAP, CMCD_JSON, CMCD_KEYS, CMCD_MIME_TYPE, CMCD_OBJECT, CMCD_PARAM, CMCD_QUERY, CMCD_RECORDED_REPORT_MODE_EVENT, CMCD_RECORDED_REPORT_MODE_HEADER, CMCD_RECORDED_REPORT_MODE_QUERY, CMCD_RECORDED_REQUEST_TYPE_EVENT, CMCD_RECORDED_REQUEST_TYPE_MANIFEST, CMCD_RECORDED_REQUEST_TYPE_SEGMENT, CMCD_RECORDED_REQUEST_TYPE_UNKNOWN, CMCD_REQUEST, CMCD_REQUEST_KEYS, CMCD_REQUEST_MODE, CMCD_RESPONSE_KEYS, CMCD_SESSION, CMCD_STATUS, CMCD_V1, CMCD_V1_KEYS, CMCD_V2, CMCD_VALIDATION_SEVERITY_ERROR, CMCD_VALIDATION_SEVERITY_WARNING, CmcdEventType, CmcdHeaderField, CmcdObjectType, CmcdPlayerState, CmcdRecordedReportMode, CmcdRecordedRequestType, CmcdReportRecorder, CmcdReporter, CmcdReportingMode, CmcdStreamType, CmcdStreamingFormat, CmcdTransmissionMode, CmcdValidationSeverity, appendCmcdHeaders, appendCmcdQuery, createFetchTransport, createXhrTransport, decodeCmcd, encodeCmcd, fromCmcdHeaders, fromCmcdQuery, fromCmcdUrl, groupCmcdHeaders, isCmcdCustomKey, isCmcdEventKey, isCmcdRequestKey, isCmcdResponseReceivedKey, isCmcdV1Data, isCmcdV1Key, isCmcdV2Data, prepareCmcdData, toCmcdHeaders, toCmcdQuery, toCmcdUrl, toCmcdValue, validateCmcd, validateCmcdEventReport, validateCmcdEvents, validateCmcdHeaders, validateCmcdKeys, validateCmcdRequest, validateCmcdStructure, validateCmcdValues };
//# sourceMappingURL=index.js.map